import { createAction, createAsyncThunk } from '@reduxjs/toolkit';
import { graphqlRequest, hasGraphQLNetworkError } from '~/adapters/graphqlRequest';
import { normalize } from 'normalizr';
import * as schemas from '../schemas';
import { benchmarkQuestionListFrom, benchmarkSettingListFrom } from '../transformers/benchmark.transformer';
import * as transformers from '../transformers';
import {
  AssociatedSurveyQuestion,
  BenchmarkAttributeMappingResult,
  BenchmarkAttrValueMappingResult,
  CompaniesSettings,
  DelBenchmarkQuestionAssociationInput,
  SurveyQuestionResult,
  UpdateBenchmarkAttrValueMappingResult,
} from '~/interfaces/benchmark.interface';
import { EmployeeAttributes } from '~/interfaces/employee.interface';
import { RootState } from '../store';
import {
  AssociateBenchmarkAttributeDocument,
  AssociateSurveyQuestionsWithBenchmarkQuestionDocument,
  Benchmark,
  BenchmarkAttributeSetting,
  BenchmarkQuestion,
  DeleteBenchmarkAttributeAssociationDocument,
  DeleteBenchmarkAttributeAssociationMutationResult,
  DeleteBenchmarkQuestionAssociationsDocument,
  DeleteBenchmarkQuestionAssociationsMutationResult,
  FetchAllBenchmarkAttributesDocument,
  FetchAllBenchmarkAttributesQueryResult,
  FetchAvailableBenchmarksDocument,
  FetchAvailableBenchmarksQueryResult,
  FetchBenchmarkQuestionsDocument,
  FetchBenchmarkQuestionsQueryResult,
  FetchBenchmarksDocument,
  FetchCompaniesBenchmarkSettingsDocument,
  FetchCompaniesBenchmarkSettingsQueryResult,
  FetchCompanyBenchmarkSettingsDocument,
  FetchCompanyEmployeeAttributesDocument,
  FetchCompanyEmployeeAttributesQueryResult,
  FetchMappedQuestionsForBenchmarkQuestionDocument,
  FetchMappedQuestionsForBenchmarkQuestionQueryResult,
  FetchUnmappedAttributeValuesDocument,
  FetchUntaggedSurveyQuestionsDocument,
  FetchUntaggedSurveyQuestionsWithAllIdsDocument,
  MapValuesToBenchmarkAttributeValueDocument,
  MapValuesToBenchmarkAttributeValueMutationResult,
  UpdateCompanyDefaultAttributeValueDocument,
  ValueIds,
} from '~/gql/graphql';
import { BenchmarkResult } from '~/interfaces/survey.interface';

const fetchAllBenchmarkAttributes = createAsyncThunk('benchmark/fetchAllBenchmarkAttributes', async () => {
  const res = await graphqlRequest<FetchAllBenchmarkAttributesQueryResult>(FetchAllBenchmarkAttributesDocument);
  return normalize(res.data?.findAllBenchmarkAttributes, [schemas.benchmarkAttribute]);
});

const fetchCompaniesBenchmarkSettings = createAsyncThunk<CompaniesSettings, { benchmarkAttributeId: number }>(
  'benchmark/fetchCompaniesBenchmarkSettings',
  async ({ benchmarkAttributeId }, { rejectWithValue }) => {
    const res = await graphqlRequest<FetchCompaniesBenchmarkSettingsQueryResult>(
      FetchCompaniesBenchmarkSettingsDocument,
      { benchmarkAttributeId },
    );
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    const data = res.data?.findAllCompaniesBenchmarkSettings;
    if (!data?.length) return rejectWithValue('No data found');
    const benchmarkSettings = benchmarkSettingListFrom(data);

    return normalize(benchmarkSettings, [schemas.companyBenchmarkSetting]);
  },
);

const fetchCompanyEmployeeAttributes = createAsyncThunk<
  EmployeeAttributes,
  { companyId: number },
  { state: RootState }
>('benchmark/fetchCompanyEmployeeAttributes', async ({ companyId }, { getState, rejectWithValue }) => {
  const account = getState().account;
  const res = await graphqlRequest<FetchCompanyEmployeeAttributesQueryResult>(FetchCompanyEmployeeAttributesDocument, {
    companyId,
  });
  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
  const rawAttributes = res.data?.findCompanyEmployeeAttributes;
  if (!rawAttributes?.length) return rejectWithValue('No data found');

  const employeeAttributes = transformers.employeeAttributesFrom(rawAttributes, {
    defaultLang: account.lang,
  });
  const company = {
    id: companyId,
    employeeAttributes,
  };
  return normalize(company, schemas.company);
});

const associateBenchmarkAttribute = createAsyncThunk<
  BenchmarkAttributeMappingResult,
  { companyId: number; attributeId: number; benchmarkAttributeId: number }
>(
  'benchmark/associateBenchmarkAttribute',
  async ({ companyId, attributeId, benchmarkAttributeId }, { rejectWithValue }) => {
    const res = await graphqlRequest<any>(AssociateBenchmarkAttributeDocument, {
      attributeId,
      benchmarkAttributeId,
    });
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    const data = res.data?.associateAttributeWithBenchmarkAttribute;
    if (!data) return rejectWithValue('No data found');
    return { ...data, companyId };
  },
);

const updateCompanyDefaultAttributeValue = createAsyncThunk<
  BenchmarkAttributeMappingResult,
  { companyId: number; benchmarkAttributeValueId: number; ungroupedValue?: string }
>(
  'benchmark/updateCompanyDefaultAttributeValue',
  async ({ companyId, benchmarkAttributeValueId, ungroupedValue }, { rejectWithValue }) => {
    const res = await graphqlRequest<any>(UpdateCompanyDefaultAttributeValueDocument, {
      companyId,
      benchmarkAttributeValueId,
      ungroupedValue,
    });
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    const data = res.data?.updateCompanyBenchmarkAttributeValue;
    if (!data) return rejectWithValue('No data found');
    return { ...data, companyId };
  },
);

const fetchUnmappedAttributeValues = createAsyncThunk<
  { unmappedAttributeValues: string[]; benchmarkAttributeId: number },
  { benchmarkAttributeId: number }
>('benchmark/fetchUnmappedAttributeValues', async ({ benchmarkAttributeId }, { rejectWithValue }) => {
  const res = await graphqlRequest<any>(FetchUnmappedAttributeValuesDocument, {
    benchmarkAttributeId,
  });
  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
  const data = res.data?.findUnmappedAttributeValues;

  return { unmappedAttributeValues: data, benchmarkAttributeId };
});

const mapValuesToBenchmarkAttributeValue = createAsyncThunk<
  BenchmarkAttrValueMappingResult,
  { benchmarkAttributeName: string; benchmarkAttributeValueId: number; names: string[] },
  { state: RootState }
>(
  'benchmark/mapValuesToBenchmarkAttributeValue',
  async ({ benchmarkAttributeName, benchmarkAttributeValueId, names }, { getState, rejectWithValue }) => {
    const res = await graphqlRequest<MapValuesToBenchmarkAttributeValueMutationResult>(
      MapValuesToBenchmarkAttributeValueDocument,
      {
        benchmarkAttributeValueId,
        names,
      },
    );
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    const data = res.data?.addBenchmarkAttributeValueAltNames;
    if (!data?.length) return rejectWithValue('Map values to benchmark attribute value failed with no data returned');

    const benchmarkAttribute = getState().benchmarks.entities.attributes[benchmarkAttributeName];
    return { benchmarkAttributeName, benchmarkAttributeId: benchmarkAttribute.id, benchmarkAttributeValueId, names };
  },
);

const updateValueToBenchmarkAttrValueMapping = createAsyncThunk<
  UpdateBenchmarkAttrValueMappingResult,
  {
    benchmarkAttributeName: string;
    newBenchmarkAttributeValueId: number;
    oldBenchmarkAttributeValueId: number;
    name: string;
  }
>(
  'benchmark/updateValueToBenchmarkAttrValueMapping',
  async (
    { benchmarkAttributeName, newBenchmarkAttributeValueId, oldBenchmarkAttributeValueId, name },
    { rejectWithValue },
  ) => {
    const res = await graphqlRequest<MapValuesToBenchmarkAttributeValueMutationResult>(
      MapValuesToBenchmarkAttributeValueDocument,
      {
        benchmarkAttributeValueId: newBenchmarkAttributeValueId,
        names: [name],
      },
    );
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    const data = res.data?.addBenchmarkAttributeValueAltNames;
    if (!data?.length) return rejectWithValue('Update mapping failed with no data returned');

    return {
      benchmarkAttributeName,
      newBenchmarkAttributeValueId,
      oldBenchmarkAttributeValueId,
      name,
    };
  },
);

const deleteBenchmarkAttributeAssociation = createAsyncThunk<
  { companyId: number; attributeId: number | null; benchmarkAttributeId: number },
  { companyId: number; attributeId: number; benchmarkAttributeId: number }
>(
  'benchmark/deleteBenchmarkAttributeAssociation',
  async ({ companyId, attributeId, benchmarkAttributeId }, { rejectWithValue }) => {
    const res = await graphqlRequest<DeleteBenchmarkAttributeAssociationMutationResult>(
      DeleteBenchmarkAttributeAssociationDocument,
      {
        attributeId,
        benchmarkAttributeId,
      },
    );
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

    return { companyId, attributeId: null, benchmarkAttributeId };
  },
);

const fetchBenchmarkQuestions = createAsyncThunk<
  { benchmarkQuestions: { [questionId: number]: BenchmarkQuestion }; surveyTypeId: number },
  { surveyTypeId: number },
  { state: RootState }
>('benchmark/fetchBenchmarkQuestions', async ({ surveyTypeId }, { rejectWithValue }) => {
  const res = await graphqlRequest<FetchBenchmarkQuestionsQueryResult>(FetchBenchmarkQuestionsDocument, {
    surveyTypeId,
  });
  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
  if (!res.data?.findBenchmarkQuestions?.length) return rejectWithValue('No benchmark question found');
  const rawQuestions = benchmarkQuestionListFrom(res.data?.findBenchmarkQuestions);
  const benchmarkQuestions = normalize(rawQuestions, [schemas.benchmarkQuestion]);

  return { benchmarkQuestions, surveyTypeId };
});

const fetchUntaggedSurveyQuestions = createAsyncThunk<
  SurveyQuestionResult,
  {
    surveyTypeId: number;
    benchmarkQuestionId: number;
    startYear: number;
    filterText?: string;
    filterYear?: number;
    offset?: number;
    limit?: number;
    selectedCompanyId?: string | number;
  }
>(
  'benchmark/fetchUntaggedSurveyQuestions',
  async (
    { surveyTypeId, benchmarkQuestionId, startYear, filterText, filterYear, offset, limit, selectedCompanyId },
    { rejectWithValue },
  ) => {
    const validFilterText = filterText ? filterText.trim().toLowerCase() : '';
    // For filterText not empty scenario, need support additional 'Select All' button, so need all untagged question ids
    const query = validFilterText
      ? FetchUntaggedSurveyQuestionsWithAllIdsDocument
      : FetchUntaggedSurveyQuestionsDocument;
    const res = await graphqlRequest<any>(query, {
      surveyQuestionInput: {
        surveyTypeId,
        benchmarkQuestionId,
        startYear,
        filterText: validFilterText,
        filterYear,
        offset,
        limit,
        companyId: selectedCompanyId !== 0 ? selectedCompanyId : undefined,
      },
    });
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    if (!res.data?.findUntaggedSurveyQuestions?.totalCount) return rejectWithValue('No untagged survey question found');

    return res.data?.findUntaggedSurveyQuestions;
  },
);

const associateQuestionsWithBenchmarkQuestion = createAsyncThunk<
  { benchmarkQuestionId: number; newMappings: SurveyQuestionResult[]; surveyTypeId: number },
  { benchmarkQuestionId: number; surveyQuestionIds: number[]; surveyTypeId: number }
>(
  'benchmark/associateQuestionsWithBenchmarkQuestion',
  async ({ benchmarkQuestionId, surveyQuestionIds, surveyTypeId }, { rejectWithValue }) => {
    const res = await graphqlRequest<any>(AssociateSurveyQuestionsWithBenchmarkQuestionDocument, {
      benchmarkQuestionId,
      surveyQuestionIds,
    });
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    if (!res.data?.associateSurveyQuestionsWithBenchmarkQuestion) return rejectWithValue('No data returned');

    const newMappings = res.data?.associateSurveyQuestionsWithBenchmarkQuestion.newMappings;

    return { benchmarkQuestionId, newMappings, surveyTypeId };
  },
);

const delBenchmarkQuestionAssociations = createAsyncThunk<
  DelBenchmarkQuestionAssociationInput,
  DelBenchmarkQuestionAssociationInput
>(
  'benchmark/delBenchmarkQuestionAssociations',
  async ({ benchmarkQuestionId, surveyQuestionIds, surveyTypeId }, { rejectWithValue }) => {
    const res = await graphqlRequest<DeleteBenchmarkQuestionAssociationsMutationResult>(
      DeleteBenchmarkQuestionAssociationsDocument,
      {
        surveyQuestionIds,
        benchmarkQuestionId,
      },
    );
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    if (!res.data?.deleteSurveyQuestionAssociations)
      return rejectWithValue('Delete benchmark question association failed');

    return { benchmarkQuestionId, surveyQuestionIds, surveyTypeId };
  },
);

const fetchAvailableBenchmarkList = createAsyncThunk(
  'benchmark/fetchAvailableBenchmarkList',
  async (_, { rejectWithValue }) => {
    const res = await graphqlRequest<FetchAvailableBenchmarksQueryResult>(FetchAvailableBenchmarksDocument);
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    if (!res.data?.findAvailableBenchmarks?.length) return rejectWithValue('No available benchmark found');

    return res.data?.findAvailableBenchmarks;
  },
);

const fetchBenchmarks = createAsyncThunk<
  { benchmarks: BenchmarkResult },
  {
    surveyId: number;
    benchmarkPeriod: string;
    questionIds: number[];
    scope: string;
    benchmarkValueIds: ValueIds;
  }
>(
  'benchmark/fetchBenchmarks',
  async ({ surveyId, benchmarkPeriod, questionIds, scope, benchmarkValueIds }, { rejectWithValue }) => {
    const res = await graphqlRequest<any>(FetchBenchmarksDocument, {
      queryInput: {
        surveyId,
        questionIds,
        benchmarkPeriod,
        scope,
        benchmarkAttributeValueIds: benchmarkValueIds,
      },
    });
    if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
    const rawBenchmarks = res.data?.findBenchmarks;
    const benchmarks = rawBenchmarks.reduce((acc: BenchmarkResult, benchmark: Benchmark) => {
      if (!acc[surveyId]) acc[surveyId] = {};
      if (!acc[surveyId][benchmark.benchmarkPeriod]) acc[surveyId][benchmark.benchmarkPeriod] = {};
      if (!acc[surveyId][benchmark.benchmarkPeriod][benchmark.questionId])
        acc[surveyId][benchmark.benchmarkPeriod][benchmark.questionId] = [];

      acc[surveyId][benchmark.benchmarkPeriod][benchmark.questionId].push({
        id: benchmark.id,
        benchmarkQuestionId: benchmark.benchmarkQuestionId,
        scope: benchmark.scope,
        regionValueId: benchmark.regionValueId,
        countryValueId: benchmark.countryValueId,
        sizeValueId: benchmark.sizeValueId,
        industryValueId: benchmark.industryValueId,
        sectorValueId: benchmark.sectorValueId,
        // @ts-ignore @TODO Epic WT-21600
        averageScore: benchmark.averageScore,
        // @ts-ignore @TODO Epic WT-21600
        positivePercent: benchmark.positivePercent,
        // @ts-ignore @TODO Epic WT-21600
        neutralPercent: benchmark.neutralPercent,
        // @ts-ignore @TODO Epic WT-21600
        negativePercent: benchmark.negativePercent,
        // @ts-ignore @TODO Epic WT-21600
        promoter: benchmark.promoter,
        // @ts-ignore @TODO Epic WT-21600
        passive: benchmark.passive,
        // @ts-ignore @TODO Epic WT-21600
        detractor: benchmark.detractor,
        // @ts-ignore @TODO Epic WT-21600
        eNPSScore: benchmark.eNPSScore,
        totalCount: benchmark.totalCount,
        companiesCount: benchmark.companiesCount,
      });
      return acc;
    }, {});

    return { benchmarks: benchmarks };
  },
);

const fetchCompanyBenchmarkSettings = createAsyncThunk<
  { companyId: number; benchmarkAttributes: BenchmarkAttributeSetting[] },
  { companyId: number }
>('benchmark/fetchCompanyBenchmarkSettings', async ({ companyId }, { rejectWithValue }) => {
  const res = await graphqlRequest<any>(FetchCompanyBenchmarkSettingsDocument, {
    companyId,
  });
  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);

  const result = res.data?.findCompanyBenchmarkSettings?.benchmarkAttributes.reduce((acc, attribute) => {
    acc[attribute.benchmarkAttributeName] = attribute;
    return acc;
  }, {});

  return result;
});

const fetchTaggedSurveyQuestions = createAsyncThunk<
  { [questionId: number]: AssociatedSurveyQuestion; surveyTypeId: number },
  { surveyTypeId: number; benchmarkQuestionId: number }
>('benchmark/fetchTaggedSurveyQuestions', async ({ surveyTypeId, benchmarkQuestionId }, { rejectWithValue }) => {
  const res = await graphqlRequest<FetchMappedQuestionsForBenchmarkQuestionQueryResult>(
    FetchMappedQuestionsForBenchmarkQuestionDocument,
    { benchmarkQuestionId },
  );
  if (hasGraphQLNetworkError(res)) return rejectWithValue(res.error);
  const newMappings = res.data?.findMappedQuestionsForBenchmarkQuestion;

  return { benchmarkQuestionId, newMappings, surveyTypeId };
});

const updateTaggedQuestionCount = createAction(
  'benchmark/updateTaggedQuestionCount',
  ({
    surveyTypeId,
    benchmarkQuestionId,
    addedMappings = 0,
    removedMappings = 0,
  }: {
    surveyTypeId: number;
    benchmarkQuestionId: number;
    addedMappings: number;
    removedMappings: number;
  }) => {
    return {
      payload: {
        surveyTypeId,
        benchmarkQuestionId,
        addedMappings,
        removedMappings,
      },
    };
  },
);

export const benchmarkActions = {
  fetchAllBenchmarkAttributes,
  fetchCompaniesBenchmarkSettings,
  fetchCompanyEmployeeAttributes,
  associateBenchmarkAttribute,
  updateCompanyDefaultAttributeValue,
  fetchUnmappedAttributeValues,
  mapValuesToBenchmarkAttributeValue,
  updateValueToBenchmarkAttrValueMapping,
  deleteBenchmarkAttributeAssociation,
  fetchBenchmarkQuestions,
  fetchUntaggedSurveyQuestions,
  associateQuestionsWithBenchmarkQuestion,
  delBenchmarkQuestionAssociations,
  fetchAvailableBenchmarkList,
  fetchBenchmarks,
  fetchCompanyBenchmarkSettings,
  fetchTaggedSurveyQuestions,
  updateTaggedQuestionCount,
};
